/**
 * useMidi hook
 *
 * Handles fetching MIDI track data from the API and parsing it into
 * game-ready GameNote objects.
 */

import { useState, useCallback } from 'react';
import { MidiTrack } from '../types';
import { GameNote } from '../types';
import { parseMidiTrack, generateDemoTrack } from '../utils/midiParser';
import { API_BASE_URL } from '../config';

export interface UseMidiReturn {
  midiTrack: MidiTrack | null;
  gameNotes: GameNote[];
  audioUrl: string | null;
  isLoading: boolean;
  error: string | null;
  loadTrack: (trackId: string) => Promise<void>;
  loadDemoTrack: () => void;
  clearTrack: () => void;
  sectionStart: number;
  sectionEnd: number | null;
}

export function useMidi(): UseMidiReturn {
  const [midiTrack, setMidiTrack] = useState<MidiTrack | null>(null);
  const [gameNotes, setGameNotes] = useState<GameNote[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sectionStart, setSectionStart] = useState<number>(0);
  const [sectionEnd, setSectionEnd] = useState<number | null>(null);

  const loadTrack = useCallback(async (trackId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/tracks/${trackId}`);
      if (!res.ok) {
        throw new Error(`Track not found (${res.status})`);
      }

      const data = await res.json() as {
        midi_data?: MidiTrack;
        midiData?: MidiTrack;
        section_start?: number;
        section_end?: number;
        id?: string;
      };

      // Handle snake_case from DB or camelCase from service layer
      const track = data.midi_data ?? data.midiData;
      if (!track) {
        throw new Error('Track has no MIDI data');
      }

      setSectionStart(data.section_start ?? 0);
      setSectionEnd(data.section_end ?? null);
      // Use proxy URL instead of presigned S3 URL
      setAudioUrl(data.id ? `${API_BASE_URL}/audio/tracks/${data.id}` : null);

      setMidiTrack(track);
      setGameNotes(parseMidiTrack(track));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadDemoTrack = useCallback(() => {
    const demo = generateDemoTrack();
    setMidiTrack(demo);
    setGameNotes(parseMidiTrack(demo));
    setAudioUrl(null); // Demo uses generated audio
    setError(null);
  }, []);

  const clearTrack = useCallback(() => {
    setMidiTrack(null);
    setGameNotes([]);
    setAudioUrl(null);
    setError(null);
  }, []);

  return {
    midiTrack,
    gameNotes,
    audioUrl,
    isLoading,
    error,
    loadTrack,
    loadDemoTrack,
    clearTrack,
    sectionStart,
    sectionEnd,
  };
}
